import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatIsoDate, parseIsoDate } from '@hooks/useDocumentsQuery';

interface DateRangeTextInputsProps {
  start: Date | null;
  end: Date | null;
  onChange: (range: { start: Date | null; end: Date | null }) => void;
  /** Lets the sidebar refuse to apply a filter the user has not finished typing. */
  onValidityChange: (valid: boolean) => void;
}

// Calendar-aware: 2026-02-31 fails here where a \d{4}-\d{2}-\d{2} regex would pass.
const isoDate = z.iso.date();
const asText = (value: Date | null) => formatIsoDate(value) ?? '';

/** Typed YYYY-MM-DD entry sharing the calendar's filter state. A native date input discards
 *  an invalid value, so the error the AC asks for could never be shown with one. */
const DateRangeTextInputs: React.FC<DateRangeTextInputsProps> = ({
  start, end, onChange, onValidityChange,
}) => {
  const { t } = useTranslation();
  const id = useId();
  const [startText, setStartText] = useState(asText(start));
  const [endText, setEndText] = useState(asText(end));
  const [error, setError] = useState<'invalid' | 'reversed' | null>(null);

  useEffect(() => {
    // The props are the committed truth — and a URL can carry a reversed range the
    // inputs never saw typed.
    setStartText(asText(start));
    setEndText(asText(end));
    const reversed = Boolean(start && end && start > end);
    setError(reversed ? 'reversed' : null);
    onValidityChange(!reversed);
  }, [start, end, onValidityChange]);

  // A field the reader cannot see must not keep the apply button disabled.
  useEffect(() => () => onValidityChange(true), [onValidityChange]);

  const commit = (nextStart: string, nextEnd: string) => {
    // zod accepts year 0000; date-fns makes it an Invalid Date that crashes format().
    const bad = [nextStart, nextEnd].some(
      (v) => v !== '' && (!isoDate.safeParse(v).success || parseIsoDate(v) === null),
    );
    if (bad) { setError('invalid'); onValidityChange(false); return; }

    const parsedStart = nextStart ? parseIsoDate(nextStart) : null;
    const parsedEnd = nextEnd ? parseIsoDate(nextEnd) : null;
    if (parsedStart && parsedEnd && parsedStart > parsedEnd) {
      setError('reversed'); onValidityChange(false); return;
    }

    setError(null);
    onValidityChange(true);
    onChange({ start: parsedStart, end: parsedEnd });
  };

  const errorId = `${id}-error`;
  const field = (
    which: 'start' | 'end',
    value: string,
    setValue: (next: string) => void,
  ) => (
    <div className="flex-1 space-y-1">
      <Label htmlFor={`${id}-${which}`} className="text-xs">
        {t(`documents.dateInput.${which}Label`)}
      </Label>
      <Input
        id={`${id}-${which}`}
        value={value}
        placeholder={t('documents.dateInput.format')}
        aria-invalid={error !== null}
        aria-describedby={error ? errorId : undefined}
        onChange={(event) => {
          const next = event.target.value;
          setValue(next);
          commit(which === 'start' ? next : startText, which === 'end' ? next : endText);
        }}
      />
    </div>
  );

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        {field('start', startText, setStartText)}
        {field('end', endText, setEndText)}
      </div>
      {error && (
        <p id={errorId} role="alert" className="revy-cjk text-xs leading-[1.6] text-destructive">
          {t(`documents.dateInput.${error}`)}
        </p>
      )}
    </div>
  );
};

export default DateRangeTextInputs;
